/**
 * AdminBackups — UI für das neue mongodump/S3-Backup-System.
 *
 * Funktionalität:
 *   - Cloud-Konfig (Infomaniak Swiss Backup) anzeigen, ändern, testen
 *   - Manuelles Backup auslösen (mongodump + tar.gz + Cloud-Upload)
 *   - Liste lokaler & Cloud-Backups inkl. Download/Löschen
 *
 * Ergänzt das bestehende `BackupSettings` (E-Mail-Backup), ersetzt es nicht.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Cloud, CloudUpload, Database, Download, Trash2,
  CheckCircle2, AlertTriangle, RefreshCcw, Server, RotateCcw, Upload, Ghost,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Separator } from '../ui/separator';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const formatBytes = (b) => `${(Number(b || 0) / (1024 * 1024)).toFixed(2)} MB`;
const ZURICH = 'Europe/Zurich';
const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('de-CH', {
    timeZone: ZURICH,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  }).format(d);
};
const fmtDay = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('de-CH', {
    timeZone: ZURICH,
    year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(d);
};

const AdminBackups = () => {
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [cfg, setCfg] = useState({
    enabled: false,
    endpoint: '',
    access_key: '',
    secret_key: '',
    bucket: '',
    prefix: 'strongfit',
    region: 'us-east-1',
    retention_days: 30,
    notify_on_failure: true,
    notify_email: '',
    weekly_report_enabled: false,
    weekly_report_day: 0,
    weekly_report_hour: 8,
    weekly_report_last_sent: null,
    is_preview: false,
  });
  const [sendingReport, setSendingReport] = useState(false);
  const [reportPreview, setReportPreview] = useState(null);
  const [lastManifest, setLastManifest] = useState(null);

  // ⬆️ Chunked-Upload eines lokalen Backups (umgeht Proxy-Größenlimit)
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [uploadName, setUploadName] = useState('');
  // Merkt sich die zuletzt hochgeladene Datei, um deutlich zu zeigen, dass
  // man danach noch auf „Wiederherstellen" klicken muss (Hochladen ≠ Einspielen).
  const [justUploaded, setJustUploaded] = useState(null);
  const fileInputRef = React.useRef(null);

  const CHUNK_SIZE = 5 * 1024 * 1024; // 5 MB pro Teil

  // 👻 Geister-/Runaway-Serien-Bereinigung
  const [ghostLoading, setGhostLoading] = useState(false);
  const [ghostData, setGhostData] = useState(null);
  const [ghostConfirm, setGhostConfirm] = useState('');
  const [ghostDeleting, setGhostDeleting] = useState(false);
  const [ghostPhase, setGhostPhase] = useState('');

  const _ghostPhaseText = {
    scanning: 'Suche Serien…',
    backup: 'Erstelle Sicherheits-Backup…',
    deleting: 'Lösche Termine…',
  };

  // 🧹 DB-Grössen-Bereinigung (Base64-Bilder, alte Backups, Log-Retention)
  const [cleanupPreview, setCleanupPreview] = useState(null);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [cleanupConfirm, setCleanupConfirm] = useState('');
  const [cleanupRunning, setCleanupRunning] = useState(false);
  const [cleanupPhase, setCleanupPhase] = useState('');
  const [cleanupJob, setCleanupJob] = useState(null);

  const _cleanupPhaseText = {
    starting: 'Wird gestartet…',
    backup: 'Erstelle Sicherheits-Backup…',
    base64_products: 'Sichere Produktbilder ins Object Storage…',
    base64_supplements: 'Sichere Supplement-Bilder…',
    drop_backups: 'Entferne alte Migrations-Backups…',
    prune_logs: 'Lösche Logs älter als 90 Tage…',
    compact: 'Gebe Speicher frei (compact)…',
  };

  const loadCleanupPreview = async () => {
    setCleanupLoading(true);
    try {
      const r = await axios.get(`${API}/admin/backups/db-cleanup/preview`, auth());
      setCleanupPreview(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Vorschau fehlgeschlagen');
    } finally {
      setCleanupLoading(false);
    }
  };

  const pollCleanupStatus = async () => {
    try {
      const r = await axios.get(`${API}/admin/backups/db-cleanup/status`, auth());
      const job = r.data.job;
      if (!job) { setCleanupRunning(false); setCleanupPhase(''); return; }
      setCleanupJob(job);
      if (job.status === 'running') {
        setCleanupRunning(true);
        setCleanupPhase(_cleanupPhaseText[job.phase] || 'Läuft…');
        setTimeout(pollCleanupStatus, 3000);
      } else if (job.status === 'success') {
        setCleanupRunning(false); setCleanupPhase('');
        toast.success('DB-Bereinigung abgeschlossen 🎉');
        setCleanupConfirm('');
        await loadCleanupPreview();
        await load();
      } else {
        setCleanupRunning(false); setCleanupPhase('');
        toast.error(`Bereinigung fehlgeschlagen: ${job.error || 'unbekannt'}`);
      }
    } catch (e) {
      setTimeout(pollCleanupStatus, 4000);
    }
  };

  const runCleanup = async () => {
    setCleanupRunning(true);
    setCleanupPhase('Wird gestartet…');
    try {
      const r = await axios.post(`${API}/admin/backups/db-cleanup/run`, { confirm: 'DB-BEREINIGEN' }, auth());
      if (r.data.already_running) {
        toast.info('Eine Bereinigung läuft bereits – ich zeige den Fortschritt.');
      } else {
        toast.info('Bereinigung gestartet – läuft im Hintergrund. Bilder bleiben erhalten.');
      }
      pollCleanupStatus();
    } catch (e) {
      setCleanupRunning(false); setCleanupPhase('');
      toast.error(e.response?.data?.detail || 'Start fehlgeschlagen');
    }
  };

  // 🧹 Mindbody-Daten-Bereinigung (Weg B: Studio sauber trennen)
  const [mbPreview, setMbPreview] = useState(null);
  const [mbRunning, setMbRunning] = useState(false);
  const [mbPhase, setMbPhase] = useState('');
  const [mbJob, setMbJob] = useState(null);
  const [mbConfirm, setMbConfirm] = useState('');

  const _mbPhaseText = {
    starting: 'Wird gestartet…',
    backup: 'Erstelle Sicherheits-Backup…',
    strip_appointments: 'Entferne Mindbody-Felder von Terminen…',
    clear_config: 'Setze Mindbody-Konfiguration zurück…',
  };

  const loadMbCleanupPreview = async () => {
    try {
      const r = await axios.get(`${API}/admin/backups/mindbody-cleanup/preview`, auth());
      setMbPreview(r.data);
      const st = await axios.get(`${API}/admin/backups/mindbody-cleanup/status`, auth());
      if (st.data.job) {
        setMbJob(st.data.job);
        if (st.data.job.status === 'running') { setMbRunning(true); pollMbCleanupStatus(); }
      }
    } catch (e) { /* ignore */ }
  };

  const pollMbCleanupStatus = async () => {
    try {
      const r = await axios.get(`${API}/admin/backups/mindbody-cleanup/status`, auth());
      const job = r.data.job;
      if (!job) { setMbRunning(false); setMbPhase(''); return; }
      setMbJob(job);
      if (job.status === 'running') {
        setMbRunning(true);
        setMbPhase(_mbPhaseText[job.phase] || 'Läuft…');
        setTimeout(pollMbCleanupStatus, 3000);
      } else {
        setMbRunning(false); setMbPhase('');
        if (job.status === 'success') { toast.success('Mindbody-Daten bereinigt'); setMbConfirm(''); await loadMbCleanupPreview(); }
        else toast.error(`Bereinigung fehlgeschlagen: ${job.error || 'unbekannt'}`);
      }
    } catch (e) { setTimeout(pollMbCleanupStatus, 4000); }
  };

  const runMbCleanup = async () => {
    setMbRunning(true); setMbPhase('Wird gestartet…');
    try {
      const r = await axios.post(`${API}/admin/backups/mindbody-cleanup/run`, { confirm: 'MINDBODY-BEREINIGEN' }, auth());
      if (r.data.already_running) toast.info('Eine Bereinigung läuft bereits.');
      else toast.info('Mindbody-Bereinigung gestartet – läuft im Hintergrund.');
      pollMbCleanupStatus();
    } catch (e) {
      setMbRunning(false); setMbPhase('');
      toast.error(e.response?.data?.detail || 'Start fehlgeschlagen');
    }
  };

  // 🏷️ Termin-Namen synchronisieren (customer_name aus users/customers)
  const [nsPreview, setNsPreview] = useState(null);
  const [nsRunning, setNsRunning] = useState(false);
  const [nsPhase, setNsPhase] = useState('');
  const [nsJob, setNsJob] = useState(null);
  const [nsConfirm, setNsConfirm] = useState('');

  const _nsPhaseText = {
    starting: 'Wird gestartet…',
    backup: 'Erstelle Sicherheits-Backup…',
    scanning: 'Lese Kundennamen aus…',
    updating: 'Korrigiere Termin-Namen…',
  };

  const loadNameSyncPreview = async () => {
    try {
      const r = await axios.get(`${API}/admin/backups/name-sync/preview`, auth());
      setNsPreview(r.data);
      const st = await axios.get(`${API}/admin/backups/name-sync/status`, auth());
      if (st.data.job) {
        setNsJob(st.data.job);
        if (st.data.job.status === 'running') { setNsRunning(true); pollNameSyncStatus(); }
      }
    } catch (e) { /* ignore */ }
  };

  const pollNameSyncStatus = async () => {
    try {
      const r = await axios.get(`${API}/admin/backups/name-sync/status`, auth());
      const job = r.data.job;
      if (!job) { setNsRunning(false); setNsPhase(''); return; }
      setNsJob(job);
      if (job.status === 'running') {
        setNsRunning(true);
        setNsPhase(_nsPhaseText[job.phase] || 'Läuft…');
        setTimeout(pollNameSyncStatus, 3000);
      } else {
        setNsRunning(false); setNsPhase('');
        if (job.status === 'success') { toast.success('Termin-Namen synchronisiert'); setNsConfirm(''); await loadNameSyncPreview(); }
        else toast.error(`Sync fehlgeschlagen: ${job.error || 'unbekannt'}`);
      }
    } catch (e) { setTimeout(pollNameSyncStatus, 4000); }
  };

  const runNameSync = async () => {
    setNsRunning(true); setNsPhase('Wird gestartet…');
    try {
      const r = await axios.post(`${API}/admin/backups/name-sync/run`, { confirm: 'NAMEN-SYNC' }, auth());
      if (r.data.already_running) toast.info('Eine Synchronisierung läuft bereits.');
      else toast.info('Termin-Namen-Sync gestartet – läuft im Hintergrund.');
      pollNameSyncStatus();
    } catch (e) {
      setNsRunning(false); setNsPhase('');
      toast.error(e.response?.data?.detail || 'Start fehlgeschlagen');
    }
  };

  // 🧪 Restore-Probe (Test-Restore in Wegwerf-DB)
  const [probeJob, setProbeJob] = useState(null);
  const [probeRunning, setProbeRunning] = useState(false);
  const [probePhase, setProbePhase] = useState('');

  const _probePhaseText = {
    starting: 'Wird gestartet…',
    download: 'Backup wird geladen…',
    restore: 'Restauriere in Wegwerf-DB & prüfe…',
    done: 'Fertig',
  };

  const pollRestoreProbeStatus = async () => {
    try {
      const r = await axios.get(`${API}/admin/backups/restore-probe/status`, auth());
      const job = r.data.job;
      if (!job) { setProbeRunning(false); setProbePhase(''); return; }
      setProbeJob(job);
      if (job.status === 'running') {
        setProbeRunning(true);
        setProbePhase(_probePhaseText[job.phase] || 'Läuft…');
        setTimeout(pollRestoreProbeStatus, 3000);
      } else {
        setProbeRunning(false); setProbePhase('');
        if (job.status === 'success') toast.success('Restore-Probe erfolgreich – Backup ist wiederherstellbar');
        else if (job.status === 'warning') toast.warning('Restore-Probe mit Warnungen abgeschlossen');
        else if (job.status === 'skipped') toast.info('Restore-Probe übersprungen – dieser Cluster erlaubt keine Test-Datenbank. Backups sind trotzdem gültig.');
        else toast.error(`Restore-Probe fehlgeschlagen: ${job.error || 'unbekannt'}`);
      }
    } catch (e) {
      setTimeout(pollRestoreProbeStatus, 4000);
    }
  };

  const loadRestoreProbeStatus = async () => {
    try {
      const r = await axios.get(`${API}/admin/backups/restore-probe/status`, auth());
      if (r.data.job) {
        setProbeJob(r.data.job);
        if (r.data.job.status === 'running') { setProbeRunning(true); pollRestoreProbeStatus(); }
      }
    } catch (e) { /* ignore */ }
  };

  const runRestoreProbe = async () => {
    setProbeRunning(true); setProbePhase('Wird gestartet…');
    try {
      const r = await axios.post(`${API}/admin/backups/restore-probe/run`, { source: 'cloud_latest' }, auth());
      if (r.data.already_running) toast.info('Eine Restore-Probe läuft bereits.');
      else toast.info('Restore-Probe gestartet – läuft im Hintergrund (Wegwerf-DB, Live-Daten bleiben unberührt).');
      pollRestoreProbeStatus();
    } catch (e) {
      setProbeRunning(false); setProbePhase('');
      toast.error(e.response?.data?.detail || 'Start fehlgeschlagen');
    }
  };

  const previewGhost = async () => {
    setGhostLoading(true);
    try {
      const r = await axios.get(`${API}/appointments/ghost-series/preview?threshold=400`, auth());
      setGhostData(r.data);
      if (r.data.series_count === 0) toast.success('Keine Geister-Serien gefunden 🎉');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Suche fehlgeschlagen');
    } finally {
      setGhostLoading(false);
    }
  };

  const pollGhostStatus = async () => {
    try {
      const r = await axios.get(`${API}/appointments/ghost-series/status`, auth());
      const job = r.data.job;
      if (!job) { setGhostDeleting(false); setGhostPhase(''); return; }
      if (job.status === 'running') {
        setGhostDeleting(true);
        setGhostPhase(_ghostPhaseText[job.phase] || 'Läuft…');
        setTimeout(pollGhostStatus, 3000);
      } else if (job.status === 'success') {
        setGhostDeleting(false); setGhostPhase('');
        if ((job.deleted || 0) > 0) {
          toast.success(`${(job.deleted).toLocaleString('de-CH')} Termine gelöscht. Backup: ${job.backup || '—'}`);
        } else {
          toast.success(job.message || 'Keine Geister-Serien gefunden.');
        }
        setGhostData(null); setGhostConfirm('');
        await load();
      } else {
        setGhostDeleting(false); setGhostPhase('');
        toast.error(`Bereinigung fehlgeschlagen: ${job.error || 'unbekannt'}`);
      }
    } catch (e) {
      // transienter Fehler → weiter pollen
      setTimeout(pollGhostStatus, 4000);
    }
  };

  const deleteGhost = async () => {
    setGhostDeleting(true);
    setGhostPhase('Wird gestartet…');
    try {
      const r = await axios.delete(
        `${API}/appointments/ghost-series?confirm=GEISTER-LOESCHEN&threshold=400`, auth());
      if (r.data.already_running) {
        toast.info('Eine Bereinigung läuft bereits – ich zeige den Fortschritt.');
      } else {
        toast.info('Bereinigung gestartet – läuft im Hintergrund. Du kannst die Seite verlassen.');
      }
      pollGhostStatus();
    } catch (e) {
      setGhostDeleting(false); setGhostPhase('');
      toast.error(e.response?.data?.detail || 'Start fehlgeschlagen');
    }
  };

  const handleUploadFile = async (file) => {
    if (!file) return;
    if (!file.name.endsWith('.tar.gz')) {
      toast.error('Bitte eine .tar.gz Backup-Datei auswählen');
      return;
    }
    const uploadId = (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);
    const totalChunks = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
    setUploading(true);
    setUploadPct(0);
    setUploadName(file.name);

    const postChunk = (i) => {
      const start = i * CHUNK_SIZE;
      const blob = file.slice(start, Math.min(start + CHUNK_SIZE, file.size));
      const fd = new FormData();
      fd.append('file', blob, `${file.name}.part${i}`);
      fd.append('upload_id', uploadId);
      fd.append('chunk_index', String(i));
      fd.append('total_chunks', String(totalChunks));
      fd.append('filename', file.name);
      fd.append('offset', String(start));
      return axios.post(`${API}/admin/backups/upload`, fd, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data',
        },
        timeout: 120000, // 2 Min pro Chunk, sonst abbrechen & neu versuchen
      });
    };

    try {
      for (let i = 0; i < totalChunks; i++) {
        // Bis zu 4 Versuche pro Chunk mit Backoff — fängt transiente
        // Netz-/Proxy-Fehler ab, damit der Upload nicht komplett abbricht.
        let attempt = 0;
        let r = null;
        // eslint-disable-next-line no-constant-condition
        while (true) {
          try {
            r = await postChunk(i);
            break;
          } catch (err) {
            attempt += 1;
            if (attempt >= 4) throw err;
            await new Promise((res) => setTimeout(res, 1000 * attempt));
          }
        }
        setUploadPct(Math.round(((i + 1) / totalChunks) * 100));
        if (r.data.completed) {
          setJustUploaded(r.data.filename);
          toast.success('Backup hochgeladen – aber noch NICHT übernommen!', {
            duration: 12000,
            description: 'Das Hochladen legt die Datei nur ab. Klicke jetzt beim Backup auf „Wiederherstellen" (↺), damit die Daten eingespielt werden.',
            action: {
              label: 'Jetzt wiederherstellen',
              onClick: () => openRestoreDialog({ filename: r.data.filename, source: 'local' }),
            },
          });
        }
      }
      await load();
    } catch (e) {
      // Verwaiste .part-Datei serverseitig aufräumen
      try {
        await axios.post(`${API}/admin/backups/upload/abort`, { upload_id: uploadId }, auth());
      } catch (_) { /* ignore */ }
      toast.error((e.response?.data?.detail || 'Upload fehlgeschlagen') + ' – bitte erneut versuchen.');
    } finally {
      setUploading(false);
      setUploadPct(0);
      setUploadName('');
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // ♻️ Direkt-Restore aus der Backup-Liste
  const [restoreTarget, setRestoreTarget] = useState(null); // {filename, source, key}
  const [restoreConfirm, setRestoreConfirm] = useState('');
  const [restorePreview, setRestorePreview] = useState(null);
  const [restorePreviewLoading, setRestorePreviewLoading] = useState(false);
  const [restoreRunning, setRestoreRunning] = useState(false);

  const openRestoreDialog = (target) => {
    setRestoreTarget(target);
    setRestoreConfirm('');
    setRestorePreview(null);
  };

  const loadRestorePreview = async () => {
    setRestorePreviewLoading(true);
    try {
      const r = await axios.post(`${API}/admin/backups/restore`, {
        ...restoreTarget, dry_run: true,
      }, auth());
      setRestorePreview(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Vorschau fehlgeschlagen');
    } finally {
      setRestorePreviewLoading(false);
    }
  };

  const startRestore = async () => {
    setRestoreRunning(true);
    try {
      const r = await axios.post(`${API}/admin/backups/restore`, {
        ...restoreTarget, dry_run: false, confirm: restoreConfirm,
      }, auth());
      const startedAt = r.data.started_at;
      toast.info('Wiederherstellung läuft — vorher wird automatisch ein Sicherungs-Backup erstellt…');
      let finished = false;
      for (let i = 0; i < 180 && !finished; i++) {
        await new Promise((res) => setTimeout(res, 5000));
        try {
          const s = await axios.get(`${API}/admin/backups/last-run`, {
            ...auth(),
            params: { since: startedAt, run_type: 'restore' },
          });
          const run = s.data.run;
          if (run) {
            finished = true;
            if (run.status === 'success') {
              setJustUploaded(null);
              toast.success(
                `Wiederherstellung OK: ${run.collections_count} Collections, ${run.total_docs} Dokumente. Sicherung davor: ${run.safety_backup}`
              );
            } else {
              toast.error(`Wiederherstellung fehlgeschlagen: ${run.error || 'unknown'}`);
            }
          }
        } catch {
          // weiter pollen
        }
      }
      if (!finished) toast.info('Wiederherstellung läuft noch — Seite später neu laden.');
      setRestoreTarget(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Restore-Start fehlgeschlagen');
    } finally {
      setRestoreRunning(false);
    }
  };

  const WEEKDAYS = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];

  const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/backups`, auth());
      setData(r.data);
      if (r.data.config) setCfg(prev => ({ ...prev, ...r.data.config }));
    } catch (e) {
      console.error(e);
      toast.error('Backups konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);
  // Letztes Backup-Manifest laden (Frische-Anzeige)
  useEffect(() => {
    (async () => {
      try {
        const s = await axios.get(`${API}/admin/backups/last-run`, { ...auth(), params: { run_type: 'manual' } });
        if (s.data.run && s.data.run.manifest) setLastManifest(s.data.run.manifest);
      } catch (e) { /* ignore */ }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Laufenden Geister-Bereinigungs-Job nach Reload automatisch fortsetzen
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/appointments/ghost-series/status`, auth());
        if (r.data.job && r.data.job.status === 'running') pollGhostStatus();
      } catch (e) { /* ignore */ }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // DB-Bereinigung: Vorschau laden + laufenden Job nach Reload fortsetzen
  useEffect(() => {
    (async () => {
      try {
        loadCleanupPreview();
        loadRestoreProbeStatus();
        loadMbCleanupPreview();
        loadNameSyncPreview();
        const r = await axios.get(`${API}/admin/backups/db-cleanup/status`, auth());
        if (r.data.job) {
          setCleanupJob(r.data.job);
          if (r.data.job.status === 'running') pollCleanupStatus();
        }
      } catch (e) { /* ignore */ }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const previewWeeklyReport = async () => {
    try {
      const r = await axios.get(`${API}/admin/backups/weekly-report/preview`, auth());
      setReportPreview(r.data);
      toast.info('Vorschau erstellt');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Vorschau fehlgeschlagen');
    }
  };

  const sendWeeklyReportNow = async () => {
    if (!window.confirm('Wochenreport jetzt per E-Mail versenden?')) return;
    setSendingReport(true);
    try {
      const r = await axios.post(`${API}/admin/backups/weekly-report/send`, {}, auth());
      if (r.data.sent) toast.success('✅ Wochenreport verschickt');
      else toast.error('❌ Wochenreport konnte nicht versendet werden (SMTP/Empfänger prüfen)');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Versand fehlgeschlagen');
    } finally {
      setSendingReport(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/backups/cloud/config`, cfg, auth());
      toast.success('Cloud-Konfiguration gespeichert');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      const r = await axios.post(`${API}/admin/backups/cloud/test`, cfg, auth());
      if (r.data.ok) toast.success('✅ ' + (r.data.message || 'Verbindung erfolgreich'));
      else toast.error('❌ ' + (r.data.error || 'Verbindung fehlgeschlagen'));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test fehlgeschlagen');
    } finally {
      setTesting(false);
    }
  };

  const triggerBackup = async (dbOnly = false) => {
    setRunning(true);
    try {
      const r = await axios.post(`${API}/admin/backups`, { db_only: dbOnly }, auth());
      const startedAt = r.data.started_at;
      toast.info('Backup läuft im Hintergrund — das kann einige Minuten dauern…');
      // Polling bis der Hintergrund-Job fertig ist (max. 15 Min)
      let finished = false;
      for (let i = 0; i < 180 && !finished; i++) {
        await new Promise((res) => setTimeout(res, 5000));
        try {
          const s = await axios.get(`${API}/admin/backups/last-run`, {
            ...auth(),
            params: { since: startedAt },
          });
          const run = s.data.run;
          if (run) {
            finished = true;
            if (run.status === 'success') {
              const filesNote = run.files_count
                ? `, +${run.files_count} Files (${run.uploads_size_mb} MB)`
                : '';
              toast.success(
                `Backup OK: ${run.size_mb} MB, ${run.collections_count} Collections${filesNote}`
              );
              if (run.manifest) setLastManifest(run.manifest);
              const cl = run.cloud_result;
              if (cl && cl.ok) toast.success(`Cloud-Upload OK: ${cl.key}`);
              else if (cl && !cl.ok && !cl.skipped) toast.error(`Cloud-Upload fehlgeschlagen: ${cl.error}`);
            } else {
              toast.error(`Backup fehlgeschlagen: ${run.error || 'unknown'}`);
            }
          }
        } catch {
          // Polling-Fehler ignorieren, weiter versuchen
        }
      }
      if (!finished) toast.info('Backup läuft noch — Liste später mit "Neu laden" aktualisieren.');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Backup-Trigger fehlgeschlagen');
    } finally {
      setRunning(false);
    }
  };

  const deleteLocal = async (filename) => {
    if (!window.confirm(`Lokales Backup "${filename}" wirklich löschen?`)) return;
    try {
      await axios.delete(`${API}/admin/backups/${encodeURIComponent(filename)}`, auth());
      toast.success('Backup gelöscht');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Löschen fehlgeschlagen');
    }
  };

  const deleteCloud = async (key) => {
    if (!window.confirm(`Cloud-Backup "${key}" wirklich löschen?`)) return;
    try {
      await axios.delete(`${API}/admin/backups/cloud/${encodeURIComponent(key)}`, auth());
      toast.success('Cloud-Backup gelöscht');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Löschen fehlgeschlagen');
    }
  };

  const downloadLocal = (filename) => {
    const url = `${API}/admin/backups/download/${encodeURIComponent(filename)}`;
    const token = localStorage.getItem('token');
    // Header-basierter Download → fetch + Blob, dann Link klicken.
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.blob();
      })
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = filename;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => toast.error('Download fehlgeschlagen — Datei nicht (mehr) vorhanden?'));
  };

  const downloadCloud = (key, filename) => {
    const url = `${API}/admin/backups/cloud/download/${encodeURIComponent(key)}`;
    const token = localStorage.getItem('token');
    toast.info('Cloud-Download wird vorbereitet …');
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.blob();
      })
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = filename;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => toast.error('Cloud-Download fehlgeschlagen'));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        <RefreshCcw className="h-5 w-5 mr-2 animate-spin" /> Lade Backups…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-backups-root">
      {/* Status & Trigger */}
      <Card>
        <CardHeader className="flex flex-row items-start justify-between space-y-0">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Database className="h-5 w-5 text-green-600" />
              DB-Backup (mongodump + S3)
            </CardTitle>
            <CardDescription>
              Täglich um 03:00 Uhr (Europe/Zurich) — komprimiert als <code>.tar.gz</code>, lokal {data?.local_retention_days || 14} Tage, Cloud {cfg.retention_days || 30} Tage.
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={load} data-testid="reload-backups-btn">
              <RefreshCcw className="h-4 w-4 mr-1" />
              Neu laden
            </Button>
            <Button variant="outline" onClick={() => triggerBackup(true)} disabled={running} data-testid="trigger-db-backup-btn" title="Nur Datenbank, ohne Bilder/PDFs — kleine Datei">
              {running ? (
                <><RefreshCcw className="h-4 w-4 mr-1 animate-spin" /> Läuft…</>
              ) : (
                <><Database className="h-4 w-4 mr-1" /> Nur DB sichern</>
              )}
            </Button>
            <Button onClick={() => triggerBackup(false)} disabled={running} data-testid="trigger-backup-btn">
              {running ? (
                <><RefreshCcw className="h-4 w-4 mr-1 animate-spin" /> Läuft…</>
              ) : (
                <><CloudUpload className="h-4 w-4 mr-1" /> Jetzt sichern</>
              )}
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* Geister-/Runaway-Serien-Bereinigung */}
      <Card data-testid="ghost-series-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Ghost className="h-5 w-5 text-purple-600" />
            Geister-Serien bereinigen
          </CardTitle>
          <CardDescription>
            Findet fehlerhaft erzeugte Mega-Serien (mehr als 400 Termine, z. B. „unendliche" Serien bis ins Jahr 2997) und löscht sie endgültig. Vorher wird automatisch ein Datenbank-Backup erstellt.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button variant="outline" onClick={previewGhost} disabled={ghostLoading || ghostDeleting} data-testid="ghost-preview-btn">
            {ghostLoading ? <RefreshCcw className="h-4 w-4 mr-1 animate-spin" /> : <Ghost className="h-4 w-4 mr-1" />}
            {ghostLoading ? 'Suche…' : 'Geister-Serien suchen'}
          </Button>

          {ghostDeleting && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 flex items-center gap-2" data-testid="ghost-running-banner">
              <RefreshCcw className="h-4 w-4 animate-spin flex-shrink-0" />
              <span>{ghostPhase || 'Läuft…'} — läuft im Hintergrund, du kannst die Seite verlassen.</span>
            </div>
          )}

          {ghostData && ghostData.series_count > 0 && (
            <div className="space-y-3" data-testid="ghost-result">
              <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700">
                <strong>{ghostData.series_count}</strong> Runaway-Serie(n) mit insgesamt{' '}
                <strong>{ghostData.total_appointments.toLocaleString('de-CH')}</strong> Terminen gefunden.
              </div>
              <ul className="divide-y text-sm border rounded-md">
                {ghostData.series.map((s) => (
                  <li key={s.series_id} className="p-2" data-testid={`ghost-series-${s.series_id}`}>
                    <p className="font-semibold">{s.customer_name} · {s.service_name || 'Unbekannter Service'}</p>
                    <p className="text-xs text-muted-foreground font-mono">
                      {s.count.toLocaleString('de-CH')} Termine · {String(s.min_start).slice(0, 10)} → {String(s.max_start).slice(0, 10)}
                    </p>
                  </li>
                ))}
              </ul>
              <div>
                <Label className="text-sm">Zur Bestätigung <code>GEISTER-LOESCHEN</code> eingeben:</Label>
                <Input
                  value={ghostConfirm}
                  onChange={(e) => setGhostConfirm(e.target.value)}
                  placeholder="GEISTER-LOESCHEN"
                  className="font-mono mt-1"
                  data-testid="ghost-confirm-input"
                />
              </div>
              <Button
                variant="destructive"
                onClick={deleteGhost}
                disabled={ghostDeleting || ghostConfirm !== 'GEISTER-LOESCHEN'}
                data-testid="ghost-delete-btn"
              >
                {ghostDeleting ? <RefreshCcw className="h-4 w-4 mr-1 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1" />}
                {ghostDeleting ? (ghostPhase || 'Läuft…') : 'Geister-Serien endgültig löschen'}
              </Button>
              {ghostDeleting && (
                <p className="text-xs text-muted-foreground" data-testid="ghost-progress-hint">
                  Läuft im Hintergrund – du kannst die Seite verlassen und später wiederkommen.
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* DB-Grössen-Bereinigung */}
      <Card data-testid="db-cleanup-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5 text-emerald-600" />
            Datenbank verkleinern
          </CardTitle>
          <CardDescription>
            Entfernt redundante Bild-Kopien (Base64) aus der Datenbank, löscht alte Migrations-Sicherungen und technische Logs älter als 90 Tage. <strong>Bilder bleiben sichtbar</strong> — jedes Bild wird zuerst im persistenten Object Storage gesichert, erst dann wird die DB-Kopie entfernt. Vorher wird automatisch ein DB-Backup erstellt.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={loadCleanupPreview} disabled={cleanupLoading || cleanupRunning} data-testid="db-cleanup-preview-btn">
              {cleanupLoading ? <RefreshCcw className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCcw className="h-4 w-4 mr-1" />}
              {cleanupLoading ? 'Analysiere…' : 'Vorschau aktualisieren'}
            </Button>
          </div>

          {cleanupPreview && (
            <div className="space-y-2 text-sm" data-testid="db-cleanup-preview">
              <div className="rounded-md border bg-muted/40 p-3">
                <p className="font-semibold flex items-center gap-2">
                  <Server className="h-4 w-4" /> Aktuelle DB-Grösse: {cleanupPreview.db_total_mb} MB
                  <span className="text-muted-foreground font-normal">(Daten {cleanupPreview.db_data_mb} MB)</span>
                </p>
              </div>
              <ul className="divide-y border rounded-md">
                <li className="p-2 flex justify-between">
                  <span>Redundante Base64-Bilder (Produkte)</span>
                  <Badge variant={cleanupPreview.base64_products ? 'default' : 'secondary'}>{cleanupPreview.base64_products}</Badge>
                </li>
                <li className="p-2 flex justify-between">
                  <span>Redundante Base64-Bilder (Supplements)</span>
                  <Badge variant={cleanupPreview.base64_supplements ? 'default' : 'secondary'}>{cleanupPreview.base64_supplements}</Badge>
                </li>
                {(cleanupPreview.old_backup_collections || []).map((c) => (
                  <li key={c.collection} className="p-2 flex justify-between" data-testid={`db-cleanup-backup-${c.collection}`}>
                    <span className="font-mono text-xs">{c.collection}</span>
                    <Badge variant={c.docs ? 'default' : 'secondary'}>{c.docs.toLocaleString('de-CH')} Docs</Badge>
                  </li>
                ))}
                {(cleanupPreview.log_collections || []).map((c) => (
                  <li key={c.collection} className="p-2 flex justify-between" data-testid={`db-cleanup-log-${c.collection}`}>
                    <span className="font-mono text-xs">{c.collection} <span className="text-muted-foreground">(&gt; 90 Tage)</span></span>
                    <Badge variant={c.older_than_90d ? 'default' : 'secondary'}>{c.older_than_90d.toLocaleString('de-CH')} / {c.total.toLocaleString('de-CH')}</Badge>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {cleanupRunning && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 flex items-center gap-2" data-testid="db-cleanup-running-banner">
              <RefreshCcw className="h-4 w-4 animate-spin flex-shrink-0" />
              <span>{cleanupPhase || 'Läuft…'} — läuft im Hintergrund, du kannst die Seite verlassen.</span>
            </div>
          )}

          {cleanupJob && cleanupJob.status === 'success' && cleanupJob.result && !cleanupRunning && (
            <div className="rounded-md border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-800 space-y-1" data-testid="db-cleanup-result">
              <p className="font-semibold flex items-center gap-2"><CheckCircle2 className="h-4 w-4" /> Letzte Bereinigung erfolgreich</p>
              <p>Produkt-Bilder gesichert & bereinigt: <strong>{cleanupJob.result.products?.stripped || 0}</strong> · unsicher übersprungen: {cleanupJob.result.products?.skipped_unsafe || 0}</p>
              <p>Supplement-Bilder bereinigt: <strong>{cleanupJob.result.supplements?.stripped || 0}</strong></p>
              <p>Logs gelöscht (&gt; 90 Tage): <strong>{(cleanupJob.result.pruned_logs?.total_deleted || 0).toLocaleString('de-CH')}</strong></p>
              {cleanupJob.result.db_total_mb_after != null && (
                <p>DB-Grösse danach: <strong>{cleanupJob.result.db_total_mb_after} MB</strong></p>
              )}
              {cleanupJob.backup && <p className="text-xs">Sicherheits-Backup: <code>{cleanupJob.backup}</code></p>}
            </div>
          )}

          <Separator />
          <div>
            <Label className="text-sm">Zur Bestätigung <code>DB-BEREINIGEN</code> eingeben:</Label>
            <Input
              value={cleanupConfirm}
              onChange={(e) => setCleanupConfirm(e.target.value)}
              placeholder="DB-BEREINIGEN"
              className="font-mono mt-1"
              data-testid="db-cleanup-confirm-input"
            />
          </div>
          <Button
            variant="destructive"
            onClick={runCleanup}
            disabled={cleanupRunning || cleanupConfirm !== 'DB-BEREINIGEN'}
            data-testid="db-cleanup-run-btn"
          >
            {cleanupRunning ? <RefreshCcw className="h-4 w-4 mr-1 animate-spin" /> : <Database className="h-4 w-4 mr-1" />}
            {cleanupRunning ? (cleanupPhase || 'Läuft…') : 'Datenbank jetzt bereinigen'}
          </Button>
        </CardContent>
      </Card>

      {/* Mindbody-Daten-Bereinigung */}
      <Card data-testid="mindbody-cleanup-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trash2 className="h-5 w-5 text-orange-600" />
            Mindbody-Daten bereinigen
          </CardTitle>
          <CardDescription>
            Trennt dieses Studio sauber von Mindbody: entfernt die alten <code>mindbody_*</code>-Marker von den Terminen, deaktiviert den „Push to Mindbody"-Schalter bei allen Services, leert die Mappings und setzt die Mindbody-Verbindung zurück. <strong>Die Mindbody-Schnittstelle bleibt im System erhalten</strong> (für künftige Kunden wieder verbindbar). Vorher wird automatisch ein DB-Backup erstellt.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {mbPreview && (
            <ul className="divide-y border rounded-md text-sm" data-testid="mindbody-cleanup-preview">
              <li className="p-2 flex justify-between">
                <span>Termine mit Mindbody-Feldern</span>
                <Badge variant={mbPreview.appointments_with_mindbody_fields ? 'default' : 'secondary'}>{mbPreview.appointments_with_mindbody_fields.toLocaleString('de-CH')}</Badge>
              </li>
              <li className="p-2 flex justify-between">
                <span>Services mit „Push to Mindbody"</span>
                <Badge variant={mbPreview.services_push_enabled ? 'default' : 'secondary'}>{mbPreview.services_push_enabled}</Badge>
              </li>
              <li className="p-2 flex justify-between">
                <span>Mappings (Staff / Services)</span>
                <Badge variant={(mbPreview.staff_mappings + mbPreview.service_mappings) ? 'default' : 'secondary'}>{mbPreview.staff_mappings} / {mbPreview.service_mappings}</Badge>
              </li>
              <li className="p-2 flex justify-between">
                <span>Mindbody-Verbindung</span>
                <Badge variant={mbPreview.settings_has_api_key ? 'default' : 'secondary'}>{mbPreview.settings_has_api_key ? 'verbunden' : 'nicht verbunden'}</Badge>
              </li>
            </ul>
          )}

          {mbPreview && mbPreview.appointments_with_mindbody_fields === 0 && !mbPreview.services_push_enabled && !mbPreview.settings_has_api_key && (
            <div className="rounded-md border border-emerald-300 bg-emerald-50 p-2 text-xs text-emerald-800" data-testid="mindbody-cleanup-clean">
              ✓ Keine Mindbody-Reste gefunden – dieses Studio ist bereits sauber getrennt.
            </div>
          )}

          {mbRunning && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 flex items-center gap-2" data-testid="mindbody-cleanup-running-banner">
              <RefreshCcw className="h-4 w-4 animate-spin flex-shrink-0" />
              <span>{mbPhase || 'Läuft…'} – läuft im Hintergrund.</span>
            </div>
          )}

          {mbJob && mbJob.status === 'success' && mbJob.result && !mbRunning && (
            <div className="rounded-md border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-800 space-y-1" data-testid="mindbody-cleanup-result">
              <p className="font-semibold flex items-center gap-2"><CheckCircle2 className="h-4 w-4" /> Letzte Bereinigung erfolgreich</p>
              <p>Termine bereinigt: <strong>{(mbJob.result.appointments?.modified || 0).toLocaleString('de-CH')}</strong></p>
              <p>Services zurückgesetzt: <strong>{mbJob.result.config?.services_reset || 0}</strong> · Mappings gelöscht: {(mbJob.result.config?.staff_mappings_deleted || 0) + (mbJob.result.config?.service_mappings_deleted || 0)}</p>
              {mbJob.backup && <p className="text-xs">Sicherheits-Backup: <code>{mbJob.backup}</code></p>}
            </div>
          )}

          <Separator />
          <div>
            <Label className="text-sm">Zur Bestätigung <code>MINDBODY-BEREINIGEN</code> eingeben:</Label>
            <Input
              value={mbConfirm}
              onChange={(e) => setMbConfirm(e.target.value)}
              placeholder="MINDBODY-BEREINIGEN"
              className="font-mono mt-1"
              data-testid="mindbody-cleanup-confirm-input"
            />
          </div>
          <Button
            variant="destructive"
            onClick={runMbCleanup}
            disabled={mbRunning || mbConfirm !== 'MINDBODY-BEREINIGEN'}
            data-testid="mindbody-cleanup-run-btn"
          >
            {mbRunning ? <RefreshCcw className="h-4 w-4 mr-1 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1" />}
            {mbRunning ? (mbPhase || 'Läuft…') : 'Mindbody-Daten jetzt bereinigen'}
          </Button>
        </CardContent>
      </Card>

      {/* Termin-Namen synchronisieren */}
      <Card data-testid="name-sync-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCcw className="h-5 w-5 text-sky-600" />
            Termin-Namen synchronisieren
          </CardTitle>
          <CardDescription>
            Setzt den angezeigten Namen (<code>customer_name</code>) jedes Termins wieder auf den echten Namen aus dem Kundenprofil (über die feste <code>customer_id</code>). Behebt veraltete/falsche Etiketten, die bei der FLEX-Entkopplung auf 2./3. Teilnehmer kopiert wurden (z. B. „irene perlini" auf Sonias Termin). <strong>Die Buchung selbst wird nie verändert</strong> – nur der Anzeigename. Vorher wird automatisch ein DB-Backup erstellt. Empfehlung: <strong>nach</strong> der FLEX-Entkopplung ausführen.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button variant="outline" onClick={loadNameSyncPreview} disabled={nsRunning} data-testid="name-sync-preview-btn">
            <RefreshCcw className="h-4 w-4 mr-1" /> Vorschau aktualisieren
          </Button>

          {nsPreview && (
            <ul className="divide-y border rounded-md text-sm" data-testid="name-sync-preview">
              <li className="p-2 flex justify-between">
                <span>Termine mit falschem Namen</span>
                <Badge variant={nsPreview.wrong_name ? 'default' : 'secondary'}>{(nsPreview.wrong_name || 0).toLocaleString('de-CH')}</Badge>
              </li>
              <li className="p-2 flex justify-between">
                <span>Termine ohne Namen (leer)</span>
                <Badge variant={nsPreview.empty_name ? 'default' : 'secondary'}>{(nsPreview.empty_name || 0).toLocaleString('de-CH')}</Badge>
              </li>
              <li className="p-2 flex justify-between font-semibold">
                <span>Wird korrigiert</span>
                <Badge variant={nsPreview.will_fix ? 'default' : 'secondary'}>{(nsPreview.will_fix || 0).toLocaleString('de-CH')}</Badge>
              </li>
              <li className="p-2 flex justify-between text-gray-500">
                <span>Bereits korrekt</span>
                <span>{(nsPreview.already_correct || 0).toLocaleString('de-CH')}</span>
              </li>
              <li className="p-2 flex justify-between text-gray-500">
                <span>Unberührt (kein Kunden-Datensatz)</span>
                <span>{(nsPreview.unresolvable_appointments || 0).toLocaleString('de-CH')}</span>
              </li>
            </ul>
          )}

          {nsPreview && nsPreview.will_fix === 0 && (
            <div className="rounded-md border border-emerald-300 bg-emerald-50 p-2 text-xs text-emerald-800" data-testid="name-sync-clean">
              ✓ Alle auflösbaren Termin-Namen sind bereits korrekt.
            </div>
          )}

          {nsRunning && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 flex items-center gap-2" data-testid="name-sync-running-banner">
              <RefreshCcw className="h-4 w-4 animate-spin flex-shrink-0" />
              <span>{nsPhase || 'Läuft…'} – läuft im Hintergrund.</span>
            </div>
          )}

          {nsJob && nsJob.status === 'success' && nsJob.result && !nsRunning && (
            <div className="rounded-md border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-800 space-y-1" data-testid="name-sync-result">
              <p className="font-semibold flex items-center gap-2"><CheckCircle2 className="h-4 w-4" /> Letzte Synchronisierung erfolgreich</p>
              <p>Termin-Namen korrigiert: <strong>{(nsJob.result.modified || 0).toLocaleString('de-CH')}</strong></p>
              {nsJob.backup && <p className="text-xs">Sicherheits-Backup: <code>{nsJob.backup}</code></p>}
            </div>
          )}

          <Separator />
          <div>
            <Label className="text-sm">Zur Bestätigung <code>NAMEN-SYNC</code> eingeben:</Label>
            <Input
              value={nsConfirm}
              onChange={(e) => setNsConfirm(e.target.value)}
              placeholder="NAMEN-SYNC"
              className="font-mono mt-1"
              data-testid="name-sync-confirm-input"
            />
          </div>
          <Button
            onClick={runNameSync}
            disabled={nsRunning || nsConfirm !== 'NAMEN-SYNC'}
            data-testid="name-sync-run-btn"
          >
            {nsRunning ? <RefreshCcw className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCcw className="h-4 w-4 mr-1" />}
            {nsRunning ? (nsPhase || 'Läuft…') : 'Termin-Namen jetzt synchronisieren'}
          </Button>
        </CardContent>
      </Card>

      {/* Restore-Probe */}
      <Card data-testid="restore-probe-card">        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RotateCcw className="h-5 w-5 text-indigo-600" />
            Restore-Probe (Wiederherstellbarkeit testen)
          </CardTitle>
          <CardDescription>
            Prüft, ob sich das neueste Cloud-Backup vollständig wiederherstellen lässt. Der Test restauriert in eine <strong>Wegwerf-Datenbank</strong> und löscht sie danach wieder – <strong>die Live-Daten werden nie berührt</strong>. Läuft zusätzlich automatisch wöchentlich (So 04:30).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button
            onClick={runRestoreProbe}
            disabled={probeRunning}
            data-testid="restore-probe-run-btn"
          >
            {probeRunning ? <RefreshCcw className="h-4 w-4 mr-1 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-1" />}
            {probeRunning ? (probePhase || 'Läuft…') : 'Neueste Cloud-Sicherung jetzt testen'}
          </Button>

          {probeRunning && (
            <div className="rounded-md border border-indigo-300 bg-indigo-50 p-3 text-sm text-indigo-800 flex items-center gap-2" data-testid="restore-probe-running-banner">
              <RefreshCcw className="h-4 w-4 animate-spin flex-shrink-0" />
              <span>{probePhase || 'Läuft…'} – läuft im Hintergrund, du kannst die Seite verlassen.</span>
            </div>
          )}

          {probeJob && !probeRunning && probeJob.result && (
            <div
              className={`rounded-md border p-3 text-sm space-y-1 ${probeJob.status === 'success' ? 'border-emerald-300 bg-emerald-50 text-emerald-800' : probeJob.status === 'warning' ? 'border-amber-300 bg-amber-50 text-amber-800' : 'border-rose-300 bg-rose-50 text-rose-800'}`}
              data-testid="restore-probe-result"
            >
              <div className="font-semibold flex items-center gap-2">
                {probeJob.result.verified
                  ? <><CheckCircle2 className="h-4 w-4" /> Wiederherstellbar bestätigt</>
                  : <><AlertTriangle className="h-4 w-4" /> Prüfung mit Auffälligkeiten</>}
                <Badge variant="outline" data-testid="restore-probe-verified">
                  {probeJob.result.verified ? 'OK' : 'PRÜFEN'}
                </Badge>
              </div>
              <p>Quelle: <code>{probeJob.backup?.filename || '—'}</code> ({probeJob.backup?.source || '—'})</p>
              <p>Restauriert: <strong>{probeJob.result.collections_restored}</strong> Collections · <strong>{(probeJob.result.total_docs || 0).toLocaleString('de-CH')}</strong> Dokumente</p>
              <p>
                Termine: <strong>{(probeJob.result.key_counts?.appointments || 0).toLocaleString('de-CH')}</strong> ·
                Kunden: <strong>{probeJob.result.key_counts?.customers || 0}</strong> ·
                Benutzer: <strong>{probeJob.result.key_counts?.users || 0}</strong> ·
                Rechnungen: <strong>{probeJob.result.key_counts?.invoices || 0}</strong>
              </p>
              <p className="text-xs">
                Wegwerf-DB: <code>{probeJob.result.target_db}</code> (danach gelöscht) · Dauer: {probeJob.duration_seconds}s · {fmtDate(probeJob.finished_at)}
              </p>
              {probeJob.result.decode_errors?.length > 0 && (
                <p className="text-xs text-rose-700">Dekodier-Fehler: {probeJob.result.decode_errors.join('; ')}</p>
              )}
              {probeJob.result.manifest_mismatches && Object.keys(probeJob.result.manifest_mismatches).length > 0 && (
                <p className="text-xs text-amber-700">Abweichung zum Manifest: {JSON.stringify(probeJob.result.manifest_mismatches)}</p>
              )}
            </div>
          )}
          {probeJob && !probeRunning && probeJob.status === 'skipped' && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="restore-probe-skipped">
              <p className="font-semibold mb-1">ℹ️ Restore-Probe auf diesem Server nicht verfügbar</p>
              <p>{probeJob.error || 'Der Datenbank-Benutzer darf keine separate Test-Datenbank anlegen/löschen.'}</p>
            </div>
          )}
          {probeJob && !probeRunning && probeJob.status === 'error' && !probeJob.result && (
            <div className="rounded-md border border-rose-300 bg-rose-50 p-3 text-sm text-rose-800" data-testid="restore-probe-error">
              Restore-Probe fehlgeschlagen: {probeJob.error}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Cloud className="h-5 w-5 text-blue-600" />
            Cloud-Backup (Infomaniak Swiss Backup)
          </CardTitle>
          <CardDescription>
            S3-kompatibel — Backups werden zusätzlich verschlüsselt in die Schweizer Cloud geladen.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {cfg.is_preview && (
            <div
              className="rounded-md border border-amber-400/50 bg-amber-500/10 px-3 py-2 flex items-start gap-2"
              data-testid="preview-environment-warning"
            >
              <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-amber-200">
                <p className="font-semibold">Preview-Umgebung erkannt</p>
                <p>
                  Cloud-Uploads werden hier automatisch <strong>übersprungen</strong>, damit Test-Daten nicht in den Production-Bucket gelangen. Lokale Backups werden trotzdem erstellt. Aktiv wird die Cloud erst auf <code>app.strongfit.ch</code>.
                </p>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between">
            <Label htmlFor="cloud-enabled" className="flex flex-col">
              <span className="font-semibold">Cloud-Upload aktiv</span>
              <span className="text-xs text-muted-foreground">Bei jedem Backup-Lauf zu S3 hochladen</span>
            </Label>
            <Switch
              id="cloud-enabled"
              checked={!!cfg.enabled}
              onCheckedChange={(v) => setCfg(c => ({ ...c, enabled: v }))}
              data-testid="cloud-enabled-switch"
            />
          </div>

          <Separator />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Endpoint</Label>
              <Input value={cfg.endpoint} onChange={e => setCfg(c => ({ ...c, endpoint: e.target.value }))} placeholder="https://s3.swiss-backup04.infomaniak.com" data-testid="cloud-endpoint" />
            </div>
            <div>
              <Label>Bucket</Label>
              <Input value={cfg.bucket} onChange={e => setCfg(c => ({ ...c, bucket: e.target.value }))} placeholder="emergent-apps-backup" data-testid="cloud-bucket" />
            </div>
            <div>
              <Label>Access Key</Label>
              <Input value={cfg.access_key} onChange={e => setCfg(c => ({ ...c, access_key: e.target.value }))} data-testid="cloud-access-key" />
            </div>
            <div>
              <Label>Secret Key</Label>
              <Input type="password" value={cfg.secret_key} onChange={e => setCfg(c => ({ ...c, secret_key: e.target.value }))} placeholder={cfg.secret_key === '***' ? 'gesetzt — leer lassen um zu behalten' : ''} data-testid="cloud-secret-key" />
            </div>
            <div>
              <Label>App-Prefix</Label>
              <Input value={cfg.prefix} onChange={e => setCfg(c => ({ ...c, prefix: e.target.value }))} placeholder="strongfit" data-testid="cloud-prefix" />
              <p className="text-xs text-muted-foreground mt-1">
                Trennt Apps im gleichen Bucket. Empfohlen: <code>strongfit</code>
              </p>
            </div>
            <div>
              <Label>Region</Label>
              <Input value={cfg.region} onChange={e => setCfg(c => ({ ...c, region: e.target.value }))} placeholder="us-east-1" data-testid="cloud-region" />
            </div>
            <div>
              <Label>Retention (Tage)</Label>
              <Input
                type="number" min="1" max="3650"
                value={cfg.retention_days}
                onChange={e => setCfg(c => ({ ...c, retention_days: parseInt(e.target.value, 10) || 30 }))}
                data-testid="cloud-retention"
              />
            </div>
          </div>

          <Separator />

          {/* Fehler-Benachrichtigung */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label htmlFor="notify-on-failure" className="flex flex-col">
                <span className="font-semibold flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  E-Mail bei Cloud-Fehler
                </span>
                <span className="text-xs text-muted-foreground">
                  Warn-Mail wenn der S3-Upload fehlschlägt
                </span>
              </Label>
              <Switch
                id="notify-on-failure"
                checked={!!cfg.notify_on_failure}
                onCheckedChange={(v) => setCfg(c => ({ ...c, notify_on_failure: v }))}
                data-testid="notify-on-failure-switch"
              />
            </div>
            {cfg.notify_on_failure && (
              <div>
                <Label>Empfänger-E-Mail (leer = Backup-Empfänger nutzen)</Label>
                <Input
                  type="email"
                  value={cfg.notify_email}
                  onChange={e => setCfg(c => ({ ...c, notify_email: e.target.value }))}
                  placeholder="info@strongfit.ch"
                  data-testid="notify-email"
                />
              </div>
            )}
          </div>

          <Separator />

          {/* Wöchentlicher Status-Report */}
          <div className="space-y-3" data-testid="weekly-report-section">
            <div className="flex items-center justify-between">
              <Label htmlFor="weekly-report-enabled" className="flex flex-col">
                <span className="font-semibold flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  Wöchentlicher Status-Report
                </span>
                <span className="text-xs text-muted-foreground">
                  Jede Woche eine Mail mit Anzahl Backups, Cloud-Speicher und Fehlern
                </span>
              </Label>
              <Switch
                id="weekly-report-enabled"
                checked={!!cfg.weekly_report_enabled}
                onCheckedChange={(v) => setCfg(c => ({ ...c, weekly_report_enabled: v }))}
                data-testid="weekly-report-switch"
              />
            </div>

            {cfg.weekly_report_enabled && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Wochentag</Label>
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={cfg.weekly_report_day}
                    onChange={e => setCfg(c => ({ ...c, weekly_report_day: parseInt(e.target.value, 10) }))}
                    data-testid="weekly-report-day"
                  >
                    {WEEKDAYS.map((day, i) => (
                      <option key={i} value={i}>{day}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Uhrzeit (Europe/Zurich)</Label>
                  <Input
                    type="number" min="0" max="23"
                    value={cfg.weekly_report_hour}
                    onChange={e => setCfg(c => ({ ...c, weekly_report_hour: parseInt(e.target.value, 10) || 0 }))}
                    data-testid="weekly-report-hour"
                  />
                </div>
                <div className="md:col-span-2 flex gap-2 items-center">
                  <Button variant="outline" size="sm" onClick={previewWeeklyReport} data-testid="preview-report-btn">
                    Vorschau anzeigen
                  </Button>
                  <Button variant="outline" size="sm" onClick={sendWeeklyReportNow} disabled={sendingReport} data-testid="send-report-now-btn">
                    {sendingReport ? 'Sende…' : 'Jetzt senden'}
                  </Button>
                  {cfg.weekly_report_last_sent && (
                    <span className="text-xs text-muted-foreground ml-2">
                      Zuletzt verschickt: {fmtDate(cfg.weekly_report_last_sent)}
                    </span>
                  )}
                </div>

                {reportPreview && (
                  <div className="md:col-span-2 mt-2 p-3 bg-muted/50 rounded-md text-xs space-y-1 font-mono" data-testid="report-preview">
                    <p><strong>Periode:</strong> {fmtDate(reportPreview.period_from)} → {fmtDate(reportPreview.period_to)}</p>
                    <p><strong>Backup-Läufe:</strong> {reportPreview.total_runs} (lokal OK: {reportPreview.success_local}, Cloud OK: {reportPreview.success_cloud}, fehlgeschlagen: {reportPreview.failed_count})</p>
                    <p><strong>Cloud-Speicher:</strong> {reportPreview.cloud_objects_total} Objekte, {reportPreview.cloud_size_mb} MB</p>
                    {reportPreview.cloud_newest && (
                      <p><strong>Neuestes:</strong> {reportPreview.cloud_newest.filename}</p>
                    )}
                    {reportPreview.cloud_oldest && (
                      <p><strong>Ältestes:</strong> {reportPreview.cloud_oldest.filename}</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={testConnection} disabled={testing} data-testid="test-connection-btn">
              {testing ? <RefreshCcw className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
              Verbindung testen
            </Button>
            <Button onClick={saveConfig} disabled={saving} data-testid="save-cloud-config-btn">
              {saving ? 'Speichere…' : 'Konfiguration speichern'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Local Backups */}
      <Card>
        <CardHeader className="flex flex-row items-start justify-between space-y-0">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Server className="h-5 w-5 text-gray-600" />
              Lokale Backups
              <Badge variant="secondary" className="ml-2">{data?.local?.length || 0}</Badge>
            </CardTitle>
            <CardDescription>
              Rotation alle {data?.local_retention_days || 14} Tage · Enthält MongoDB + alle Datei-Uploads (Progress-Fotos, Rechnungs-PDFs, Insurance-Forms, Produkt-/Mahlzeit-Bilder)
            </CardDescription>
          </div>
          <div className="flex flex-col items-end gap-1 flex-shrink-0">
            <input
              ref={fileInputRef}
              type="file"
              accept=".tar.gz,.gz,application/gzip"
              className="hidden"
              onChange={(e) => handleUploadFile(e.target.files?.[0])}
              data-testid="backup-upload-input"
            />
            <Button
              variant="outline"
              size="sm"
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
              data-testid="backup-upload-btn"
            >
              {uploading ? (
                <><RefreshCcw className="h-4 w-4 mr-1 animate-spin" /> Lädt hoch…</>
              ) : (
                <><Upload className="h-4 w-4 mr-1" /> Backup hochladen</>
              )}
            </Button>
            <span className="text-xs text-muted-foreground">.tar.gz · beliebige Größe</span>
          </div>
        </CardHeader>
        <CardContent>
          {uploading && (
            <div className="mb-4 space-y-1" data-testid="backup-upload-progress">
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span className="font-mono truncate max-w-[70%]" title={uploadName}>{uploadName}</span>
                <span>{uploadPct}%</span>
              </div>
              <Progress value={uploadPct} />
            </div>
          )}
          {justUploaded && !uploading && (
            <div className="mb-4 rounded-md border-2 border-amber-400 bg-amber-50 p-3" data-testid="restore-hint-banner">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-amber-900">Fast fertig – Daten noch NICHT übernommen!</p>
                  <p className="text-sm text-amber-800 mt-0.5">
                    Das Hochladen legt die Datei nur ab. Um die Daten wirklich einzuspielen, musst du jetzt auf <strong>„Wiederherstellen" (↺)</strong> klicken.
                  </p>
                  <code className="mt-1 inline-block font-mono text-xs text-amber-900 break-all">{justUploaded}</code>
                </div>
                <Button
                  size="sm"
                  className="bg-amber-600 hover:bg-amber-700 text-white flex-shrink-0"
                  onClick={() => openRestoreDialog({ filename: justUploaded, source: 'local' })}
                  data-testid="restore-hint-btn"
                >
                  <RotateCcw className="h-4 w-4 mr-1" /> Jetzt wiederherstellen
                </Button>
              </div>
            </div>
          )}
          {lastManifest && lastManifest.counts && (
            <div className="mb-4 rounded-md border border-emerald-300 bg-emerald-50 p-3 text-sm" data-testid="backup-manifest">
              <div className="flex items-center gap-2 font-semibold text-emerald-800">
                <CheckCircle2 className="h-4 w-4" />
                Frische-Check des letzten Backups
              </div>
              <div className="mt-1 grid grid-cols-2 gap-x-4 gap-y-1 text-emerald-900 sm:grid-cols-4">
                <span>Kunden: <strong>{lastManifest.counts.customers}</strong></span>
                <span>Termine: <strong>{(lastManifest.counts.appointments || 0).toLocaleString('de-CH')}</strong></span>
                <span>Rechnungen: <strong>{lastManifest.counts.invoices}</strong></span>
                <span>Login-Konten: <strong>{lastManifest.counts.users}</strong></span>
              </div>
              <div className="mt-1 text-xs text-emerald-700">
                Erstellt: {fmtDate(lastManifest.backup_created_at)}
                {lastManifest.latest?.invoice_number ? ` · letzte Rechnung: ${lastManifest.latest.invoice_number}` : ''}
                {lastManifest.latest?.customer_created_at ? ` · neuester Kunde: ${fmtDay(lastManifest.latest.customer_created_at)}` : ''}
              </div>
            </div>
          )}
          {(data?.local || []).length === 0 ? (
            <div className="flex items-center gap-2 text-muted-foreground py-4">
              <AlertTriangle className="h-4 w-4" />
              Noch keine lokalen Backups vorhanden. Klicke „Jetzt sichern".
            </div>
          ) : (
            <ul className="divide-y" data-testid="local-backups-list">
              {data.local.map(b => (
                <li key={b.filename} className={`flex items-center justify-between py-2 ${b.filename === justUploaded ? 'bg-amber-50 ring-2 ring-amber-400 rounded-md px-2 -mx-2' : ''}`}>
                  <div className="min-w-0">
                    <p className="font-mono text-sm truncate" title={b.filename}>{b.filename}</p>
                    <p className="text-xs text-muted-foreground">{fmtDate(b.created_at)} · {formatBytes(b.size_bytes)}</p>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <Button size="sm" variant="outline" className={`border-amber-300 hover:bg-amber-50 ${b.filename === justUploaded ? 'bg-amber-500 text-white border-amber-500 hover:bg-amber-600 animate-pulse' : 'text-amber-600'}`} title="Wiederherstellen" onClick={() => openRestoreDialog({ filename: b.filename, source: 'local' })} data-testid={`local-restore-${b.filename}`}>
                      <RotateCcw className="h-4 w-4" />{b.filename === justUploaded ? <span className="ml-1 text-xs font-semibold">Wiederherstellen</span> : null}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => downloadLocal(b.filename)} data-testid={`local-download-${b.filename}`}>
                      <Download className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="ghost" className="text-red-500" onClick={() => deleteLocal(b.filename)} data-testid={`local-delete-${b.filename}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Cloud Backups */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Cloud className="h-5 w-5 text-blue-600" />
            Cloud-Backups (Infomaniak)
            <Badge variant="secondary" className="ml-2">{data?.cloud?.length || 0}</Badge>
          </CardTitle>
          <CardDescription>Prefix <code>{cfg.prefix}/</code> in <code>{cfg.bucket || '—'}</code></CardDescription>
        </CardHeader>
        <CardContent>
          {(data?.cloud || []).length === 0 ? (
            <div className="flex items-center gap-2 text-muted-foreground py-4">
              <AlertTriangle className="h-4 w-4" />
              Noch keine Cloud-Backups. Aktiviere den Switch & speichere die Konfiguration.
            </div>
          ) : (
            <ul className="divide-y" data-testid="cloud-backups-list">
              {data.cloud.map(o => (
                <li key={o.key} className="flex items-center justify-between py-2">
                  <div className="min-w-0">
                    <p className="font-mono text-sm truncate" title={o.key}>{o.filename}</p>
                    <p className="text-xs text-muted-foreground">{fmtDate(o.last_modified)} · {formatBytes(o.size_bytes)}</p>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <Button size="sm" variant="outline" className="text-amber-600 border-amber-300 hover:bg-amber-50" title="Wiederherstellen" onClick={() => openRestoreDialog({ filename: o.filename, source: 'cloud', key: o.key })} data-testid={`cloud-restore-${o.key}`}>
                      <RotateCcw className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="outline" title="Herunterladen" onClick={() => downloadCloud(o.key, o.filename)} data-testid={`cloud-download-${o.key}`}>
                      <Download className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="ghost" className="text-red-500" onClick={() => deleteCloud(o.key)} data-testid={`cloud-delete-${o.key}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Restore-Dialog mit Sicherheitsabfrage */}
      <Dialog open={!!restoreTarget} onOpenChange={(o) => !o && !restoreRunning && setRestoreTarget(null)}>
        <DialogContent className="max-w-lg" data-testid="restore-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <RotateCcw className="h-5 w-5" />
              Backup wiederherstellen
            </DialogTitle>
            <DialogDescription className="font-mono text-xs">{restoreTarget?.filename}</DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
              <strong>⚠️ Achtung:</strong> Alle aktuellen Daten werden durch den Stand dieses
              Backups <strong>ersetzt</strong>. Vor der Wiederherstellung wird automatisch ein
              Sicherungs-Backup des aktuellen Zustands erstellt.
            </div>

            {!restorePreview ? (
              <Button variant="outline" onClick={loadRestorePreview} disabled={restorePreviewLoading} className="w-full" data-testid="restore-dialog-preview-btn">
                {restorePreviewLoading ? <RefreshCcw className="h-4 w-4 mr-1 animate-spin" /> : <Database className="h-4 w-4 mr-1" />}
                {restorePreviewLoading ? 'Analysiere Backup…' : 'Vorschau laden (ändert nichts)'}
              </Button>
            ) : (
              <div className="p-3 bg-muted/50 rounded-md text-sm space-y-1" data-testid="restore-preview-result">
                <p>✅ Gültiges Backup:</p>
                <p><strong>{restorePreview.collections_count}</strong> Collections · <strong>{restorePreview.total_docs}</strong> Dokumente · <strong>{restorePreview.uploads_files}</strong> Upload-Dateien</p>
                {restorePreview.matches_current_db === false && (
                  <p className="text-red-600 font-semibold" data-testid="restore-db-mismatch-warning">
                    🚫 Achtung: Dieses Backup stammt aus einer anderen Umgebung
                    (Datenbank „{restorePreview.source_db}") — es enthält vermutlich
                    Test-Daten. Die Wiederherstellung wird blockiert.
                  </p>
                )}
              </div>
            )}

            <div>
              <Label className="text-sm">Zur Bestätigung den Dateinamen exakt eingeben:</Label>
              <Input
                value={restoreConfirm}
                onChange={(e) => setRestoreConfirm(e.target.value)}
                placeholder={restoreTarget?.filename}
                className="font-mono text-xs mt-1"
                data-testid="restore-confirm-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setRestoreTarget(null)} disabled={restoreRunning}>
              Abbrechen
            </Button>
            <Button
              variant="destructive"
              onClick={startRestore}
              disabled={restoreRunning || restoreConfirm !== restoreTarget?.filename}
              data-testid="restore-start-btn"
            >
              {restoreRunning ? <RefreshCcw className="h-4 w-4 mr-1 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-1" />}
              {restoreRunning ? 'Läuft…' : 'Jetzt wiederherstellen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminBackups;
